/**
 * Allowlist for host-written rich text on wedding and event sites: welcome
 * message, our story, event descriptions, FAQ answers, travel directions,
 * local tips and getting-there notes, plus their guest-language translations.
 *
 * WHY: the site renders these fields with innerHTML, and the API stores
 * whatever HTML the owner or a collaborator sends. The site is served from
 * quikrsvp.com/w/<subdomain>, the same origin as the signed-in app, so a
 * <script> or onerror= in a FAQ answer would run with access to the session
 * of any QuikRSVP user who opened the site. Quill's editor never produces such
 * markup; a direct API call can.
 *
 * WHAT IS KEPT is what the builder's Quill toolbar produces plus what a paste
 * into it brings in: an October 2026 scan of all 168 sites and their
 * translations found only p, br, strong, em, u, span, ul/ol/li, a, h1-h4 and
 * blockquote; color, background-color and font-size styles; ql-align-*,
 * ql-direction-* classes; and https links. Anything else is dropped, keeping
 * its text.
 *
 * Shared by wedding-websites/site.html (browser, window.DOMPurify) and the
 * server's save path (DOMPurify over a jsdom window), so what is stored and
 * what is shown follow one rule. Change it here, nowhere else.
 */
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory();
    else root.QRRichText = factory();
}(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    const ALLOWED_TAGS = [
        'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'sub', 'sup',
        'span', 'a', 'ul', 'ol', 'li', 'blockquote',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6'
    ];
    const ALLOWED_ATTR = ['href', 'target', 'rel', 'class', 'style', 'dir'];

    // Quill writes alignment, direction and indent as classes. ql-cursor and
    // the like are editor leftovers and go.
    const CLASS_OK = /^ql-(align|direction|indent|size|font)-[a-z0-9-]+$/;

    // The colour picker writes color/background-color; pasted text brings
    // font-size. text-align and direction are what other editors use for the
    // same thing Quill does with classes.
    const STYLE_PROPS = ['color', 'background-color', 'font-size', 'text-align', 'direction'];
    // Plain values only: colour names, #hex, rgb()/hsl(), lengths, keywords,
    // and var() -- text pasted from Instagram arrives as
    // rgba(0,0,0,var(--x,1)), and a custom property can only resolve to
    // another value. Nothing that fetches (url(), image-set()) and no escapes.
    const STYLE_VALUE_OK = /^[#\w\s.,%()+-]*$/;
    const STYLE_FUNC_BAD = /(url|expression|image|element|src)\s*\(/i;

    function cleanStyle(value) {
        return String(value || '').split(';').map(function (decl) {
            const at = decl.indexOf(':');
            if (at < 0) return '';
            const prop = decl.slice(0, at).trim().toLowerCase();
            const val = decl.slice(at + 1).trim();
            if (STYLE_PROPS.indexOf(prop) === -1 || !val) return '';
            if (!STYLE_VALUE_OK.test(val) || STYLE_FUNC_BAD.test(val)) return '';
            return prop + ': ' + val;
        }).filter(Boolean).join('; ');
    }

    // Hooks live on a DOMPurify instance, so each instance is set up once.
    const configured = typeof WeakSet === 'function' ? new WeakSet() : null;
    function configure(purify) {
        if (configured && configured.has(purify)) return;
        if (configured) configured.add(purify);
        purify.addHook('afterSanitizeAttributes', function (node) {
            if (node.hasAttribute('class')) {
                const kept = node.getAttribute('class').split(/\s+/).filter(function (c) { return CLASS_OK.test(c); });
                if (kept.length) node.setAttribute('class', kept.join(' '));
                else node.removeAttribute('class');
            }
            if (node.hasAttribute('style')) {
                const style = cleanStyle(node.getAttribute('style'));
                if (style) node.setAttribute('style', style);
                else node.removeAttribute('style');
            }
            if (node.hasAttribute('dir') && !/^(rtl|ltr|auto)$/i.test(node.getAttribute('dir'))) {
                node.removeAttribute('dir');
            }
            if (node.tagName === 'A') {
                // A link either opens a new tab without handing it our window,
                // or stays put. Nothing else.
                if (node.getAttribute('target') === '_blank') node.setAttribute('rel', 'noopener noreferrer');
                else { node.removeAttribute('target'); node.removeAttribute('rel'); }
            } else {
                node.removeAttribute('target');
                node.removeAttribute('rel');
            }
        });
    }

    /** Host rich text -> markup safe for innerHTML. */
    function sanitize(purify, html) {
        if (html == null || html === '') return '';
        configure(purify);
        return purify.sanitize(String(html), {
            ALLOWED_TAGS: ALLOWED_TAGS,
            ALLOWED_ATTR: ALLOWED_ATTR,
            ALLOW_DATA_ATTR: false,
            ALLOW_ARIA_ATTR: false
        });
    }

    return { sanitize: sanitize, ALLOWED_TAGS: ALLOWED_TAGS };
}));
